import ExcelJS from 'exceljs';

const clean = (v) => String(v ?? '').trim();
const header = (v) => clean(v).toLowerCase().replace(/[^a-z0-9]/g, '');
const REQUIRED = {
  cm: ['wono', 'receiveddate', 'faultdescription', 'location', 'remarks', 'status'],
  pm: ['workorder', 'activitycode', 'contractorlocation', 'scheduleddate'],
};
const DISPLAY = { cm: 'CM (WO.No, RECEIVED DATE, FAULT DESCRIPTION, LOCATION, REMARKS, STATUS)', pm: 'PPM (Work Order, Activity Code, Contractor Location, Scheduled Date)' };

function cellValue(v) {
  if (v instanceof Date) return v;
  if (v && typeof v === 'object') {
    if ('result' in v) return cellValue(v.result);
    if (v.richText) return v.richText.map((x) => x.text).join('');
    if ('text' in v) return v.text;
    return ''; // Excel errors and formulas without cached results are not records.
  }
  return v ?? '';
}

// Calendar dates, independent of browser timezone; reject rollover dates.
export function parseMaintenanceDate(value, date1904 = false) {
  if (value instanceof Date) return Number.isNaN(+value) ? null : value.toISOString().slice(0, 10);
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0 || value > 2958465 || (!date1904 && Math.floor(value) === 60)) return null;
    const epoch = date1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 31);
    return new Date(epoch + (Math.floor(value) - (!date1904 && value > 60 ? 1 : 0)) * 86400000).toISOString().slice(0, 10);
  }
  const s = clean(value);
  let y, m, d;
  let match = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z?)?$/);
  if (match) [, y, m, d] = match;
  else if ((match = s.match(/^(\d{1,2})[- /]([A-Za-z]{3,9})[- /](\d{2}|\d{4})$/))) {
    d = match[1]; y = +match[3]; if (y < 100) y += y < 69 ? 2000 : 1900;
    m = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(match[2].slice(0, 3).toLowerCase()) + 1;
  } else if ((match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?: \d{1,2}:\d{2}(?::\d{2})?)?$/))) {
    // pandas mixed-format default is month-first; unambiguous day-first is accepted.
    [, m, d, y] = match; if (+m > 12) [m, d] = [d, m];
  } else return null;
  const date = new Date(Date.UTC(+y, +m - 1, +d));
  return date.getUTCFullYear() === +y && date.getUTCMonth() === +m - 1 && date.getUTCDate() === +d ? date.toISOString().slice(0, 10) : null;
}

export function parseWorkbook(workbook) {
  const found = {};
  const candidates = { cm: [], pm: [] };
  workbook.eachSheet((sheet) => {
    sheet.eachRow((row, rowNumber) => {
      const headers = Array.from({ length: row.cellCount }, (_, i) => header(cellValue(row.getCell(i + 1).value)));
      for (const kind of ['cm', 'pm']) {
        if (!headers.includes(kind === 'cm' ? 'wono' : 'workorder')) continue;
        const missing = REQUIRED[kind].filter((h) => !headers.includes(h));
        if (missing.length) { candidates[kind].push(`${sheet.name}: missing ${missing.join(', ')}`); continue; }
        if (found[kind]) throw new Error(`Multiple ${kind.toUpperCase()} header tables found. Keep exactly one CM sheet and one PPM sheet.`);
        if (REQUIRED[kind].some((h) => headers.filter((x) => x === h).length !== 1)) throw new Error(`Duplicate required columns in ${sheet.name}.`);
        found[kind] = { sheet, rowNumber, headers };
      }
    });
  });
  for (const kind of ['cm', 'pm']) {
    if (!found[kind]) throw new Error(`Missing ${DISPLAY[kind]} sheet or required columns. ${candidates[kind].join('; ')}`);
  }
  if (found.cm.sheet === found.pm.sheet) throw new Error('CM and PPM must be in separate sheets.');
  const result = { month: null, cm: [], pm: [], diagnostics: [], sheets: {} };
  for (const kind of ['cm', 'pm']) {
    const { sheet, rowNumber, headers } = found[kind];
    result.sheets[kind] = sheet.name;
    let missingIds = 0;
    sheet.eachRow((row, number) => {
      if (number <= rowNumber) return;
      const values = headers.map((_, i) => cellValue(row.getCell(i + 1).value));
      if (!values.some((v) => clean(v))) return;
      const raw = Object.fromEntries(headers.map((h, i) => [h, values[i]]).filter(([h]) => h));
      const issue = (reason, skipped = true) => result.diagnostics.push({ sheet: sheet.name, row: number, reason, skipped });
      const workOrder = clean(raw[kind === 'cm' ? 'wono' : 'workorder']);
      if (!workOrder) { missingIds += 1; issue('Missing work order ID; record skipped.'); return; }
      const date = parseMaintenanceDate(raw[kind === 'cm' ? 'receiveddate' : 'scheduleddate'], workbook.properties.date1904);
      if (!date) issue('Invalid or missing date; included in totals as in the notebook, excluded from daily charts and active-day metrics. Reporting month cannot be verified.', false);
      result[kind].push({ workOrder, date, location: clean(raw[kind === 'cm' ? 'location' : 'contractorlocation']),
        description: clean(raw.faultdescription).toUpperCase(), activityCode: clean(raw.activitycode).toUpperCase(),
        remarks: clean(raw.remarks), status: clean(raw.status), raw });
    });
    if (!result[kind].length) {
      throw new Error(`No usable ${kind.toUpperCase()} records in "${sheet.name}". Records missing a work order ID: ${missingIds}.`);
    }
  }
  const months = [...new Set([...result.cm, ...result.pm].filter((r) => r.date).map((r) => r.date.slice(0, 7)))].sort();
  if (!months.length) throw new Error('Cannot detect the reporting month: no valid dates were found in CM or PPM work orders. Correct the dates in the workbook and import again.');
  if (months.length > 1) throw new Error(`Multiple reporting months found in CM / PPM work orders: ${months.join(', ')}. Upload a workbook containing one reporting month across both sheets.`);
  result.month = months[0];
  return result;
}

export async function readMaintenanceWorkbook(file) {
  if (!/\.xlsx$/i.test(file.name)) throw new Error('Upload one .xlsx Excel workbook containing CM and PPM sheets.');
  if (!file.size) throw new Error('The workbook is empty.');
  if (file.size > 30 * 1024 * 1024) throw new Error('Workbook exceeds the 30 MB browser import limit.');
  const workbook = new ExcelJS.Workbook();
  try { await workbook.xlsx.load(await file.arrayBuffer()); }
  catch { throw new Error('Cannot read this workbook. Upload a valid, unencrypted .xlsx file.'); }
  return parseWorkbook(workbook);
}
